import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pbAuthedFetch } from "../authHeaders";
import CalendarView from '../components/CalendarView';
import '../styles/calendar-ms.css';

// ---- Types ----
//
// Calendar renders campaigns (real, populated ATLAS data — the IT migration
// campaigns tracked on the Campaign Portfolio page) rather than the
// dev_projectbuilder-shaped "projects/task_schedule/milestones" schedule
// system schedule.js was ported with — that system's tables (projects,
// task_schedule, dependencies, etc.) were never migrated into ATLAS, so
// /api/schedule/portfolio always 500s. Campaigns already have exactly the
// shape a calendar needs (planned_start/planned_end) and already fit ATLAS's
// actual domain (IT migration campaigns, not generic PM projects).

interface Campaign {
  [key: string]: unknown;
  id: string;
  key: string;
  name: string;
  type: string;
  status: 'planned' | 'in_progress' | 'paused' | 'completed' | 'cancelled';
  planned_start: string | null;
  planned_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
}

interface CampaignsResponse {
  campaigns: Campaign[];
}

interface OutlookEvent {
  id: string;
  subject: string;
  location: string;
  organizer: string;
  start: string;
  end: string;
  isAllDay: boolean;
}

interface OutlookEventsResponse {
  ok: boolean;
  events: OutlookEvent[];
}

// ---- Accent mapping (Cyan Room tokens) ----

const STATUS_ACCENT: Record<string, string> = {
  completed: 'var(--ok, #2E7D5B)',
  cancelled: 'var(--risk, #B4485C)',
  paused: 'var(--gold, #B08900)',
  in_progress: 'var(--navy-2, #2A4A7F)',
  planned: 'var(--navy, #12234A)',
};

function buildItems(campaigns: Campaign[]) {
  // A campaign needs at least a planned or actual start to place on the grid —
  // one with neither has nothing to plot yet.
  return campaigns.filter((c) => c.planned_start || c.actual_start);
}

function buildConfig() {
  return {
    id: (it: Record<string, unknown>) => it.id as string,
    start: (it: Record<string, unknown>) => (it.actual_start as string) || (it.planned_start as string) || null,
    due: (it: Record<string, unknown>) => (it.actual_end as string) || (it.planned_end as string) || (it.actual_start as string) || (it.planned_start as string) || null,
    title: (it: Record<string, unknown>) => it.name as string,
    accent: (it: Record<string, unknown>) => STATUS_ACCENT[it.status as string] || 'var(--navy, #12234A)',
    isMilestone: () => false,
  };
}

export default function CalendarPage() {
  const { data, isLoading, error, refetch } = useQuery<CampaignsResponse>({
    queryKey: ['campaigns'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/campaigns');
      if (!res.ok) throw new Error(`The campaigns service returned HTTP ${res.status}.`);
      const d: CampaignsResponse = await res.json();
      if (!d || !Array.isArray(d.campaigns)) {
        throw new Error('The campaigns service returned no data.');
      }
      return d;
    },
  });

  const { data: eventsData } = useQuery<OutlookEventsResponse>({
    queryKey: ['outlook-week-events'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/integrations/outlook/events');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
    retry: false,
  });
  const myMeetings = eventsData?.events || [];

  const allCampaigns = data?.campaigns || [];
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const visibleCampaigns = selectedCampaign === 'all' ? allCampaigns : allCampaigns.filter((c) => c.id === selectedCampaign);
  const items = buildItems(visibleCampaigns);
  const config = buildConfig();

  return (
    <div className="page active" id="page-cal">
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Campaigns from the live campaign portfolio · dates reflect the campaign record</p>
            <h1 className="pagehead__title" role="heading" aria-level="1">Calendar</h1>
          </div>
          <div className="pagehead__actions">
            {allCampaigns.length > 0 && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 8 }}>
                <span className="sr-only" id="cal-campaign-filter-label">Filter by campaign</span>
                <select
                  aria-label="Filter by campaign"
                  className="btn btn--sm"
                  value={selectedCampaign}
                  onChange={(e) => setSelectedCampaign(e.target.value)}
                >
                  <option value="all">All campaigns</option>
                  {allCampaigns.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
            )}
            <button type="button" className="btn btn--sm" onClick={() => refetch()}>⟳ Refresh</button>
          </div>
        </div>
      </div>

      <div className="box" style={{ margin: '0 0 18px' }}>
        <div className="box__head">
          <div className="box__head-text">
            <p className="box__eyebrow">Campaign portfolio</p>
            <h2 className="box__title">Month view</h2>
          </div>
        </div>
        <div className="box__body box__body--flush">
          {isLoading ? (
            <div className="page-loading">
              <div className="skeleton">
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="skeleton__row">
                    <div className="skeleton__bar" style={{ width: `${60 + (i % 3) * 15}%` }} />
                  </div>
                ))}
              </div>
            </div>
          ) : error ? (
            <div className="state state--risk">
              <div className="state__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h3 className="state__title">Calendar could not be loaded</h3>
              <p className="state__body">{error instanceof Error ? error.message : 'Unknown error'}</p>
              <button type="button" className="btn" onClick={() => refetch()}>Retry</button>
            </div>
          ) : !items.length ? (
            <div className="state">
              <div className="state__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <h3 className="state__title">No scheduled campaigns yet</h3>
              <p className="state__body">Set a planned or actual start date on a campaign in Campaign Portfolio and it will appear here on a live calendar.</p>
            </div>
          ) : (
            <CalendarView items={items} config={config} />
          )}
        </div>
      </div>

      {myMeetings.length > 0 && (
        <div className="box" style={{ margin: '0 0 18px' }}>
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Outlook</p>
              <h2 className="box__title">My meetings</h2>
            </div>
          </div>
          <div className="box__body">
            {myMeetings.map((evt) => (
              <div key={evt.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--rule, #E4E7EC)' }}>
                <strong>{evt.subject}</strong>
                {evt.location ? ` · ${evt.location}` : ''}
                {evt.organizer ? ` · ${evt.organizer}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
